/**
 * What the Desktop draws, and where. Pure — no React.
 *
 * A CONTAINER is the desktop itself (`root`, the Holder root = Documents) or a
 * folder, shown in its own window. Its ITEMS are its sub-folders, its files,
 * and — on the desktop only — the Trash. The Arche Saves and My-Fias roots are
 * ordinary protected folders that happen to sit at the root; they render as
 * system icons.
 */
import type { VaultDocumentDto, VaultDocumentFolderDto } from '@/lib/api';
import { ARCHE_SAVES_ROOT, MY_FIAS_ROOT, vaultAreaOfPath, type VaultArea } from '@/lib/vault-areas';
import {
  ROOT_CONTAINER,
  TRASH_KEY,
  cellIndex,
  compareArrangeOrder,
  type Cell,
  type FolderView,
} from '../../lib/desktop-layout';
import { compareFoldersForView } from '../../lib/folder-view';

/** The default grid cell (medium icons) and canvas padding, in px. */
export const CELL_W = 104;
export const CELL_H = 100;
export const CANVAS_PAD = 12;

/** A grid's cell size and padding — a view's metrics (lib/folder-view.ts). */
export interface CellGeometry {
  cellW: number;
  cellH: number;
  pad: number;
  /** Space above the first row (the Details header). */
  top?: number;
}

export const MEDIUM_CELLS: CellGeometry = { cellW: CELL_W, cellH: CELL_H, pad: CANVAS_PAD };

export type DesktopItem =
  | {
      kind: 'folder';
      key: string;
      name: string;
      folder: VaultDocumentFolderDto;
      /** The Arche Saves / My-Fias root, drawn as a system icon. */
      system: 'arche-saves' | 'my-fias' | null;
    }
  | { kind: 'file'; key: string; name: string; doc: VaultDocumentDto }
  | { kind: 'trash'; key: typeof TRASH_KEY; name: string; full: boolean };

/** The folder a container lists: `null` for the root. */
export function folderIdOfContainer(container: string): string | null {
  return container === ROOT_CONTAINER ? null : container;
}

export function containerOfFolderId(folderId: string | null): string {
  return folderId ?? ROOT_CONTAINER;
}

/** The area a container is in. */
export function areaOfContainer(
  container: string,
  foldersById: ReadonlyMap<string, VaultDocumentFolderDto>,
): VaultArea {
  if (container === ROOT_CONTAINER) return 'documents';
  return vaultAreaOfPath(foldersById.get(container)?.path ?? null);
}

function systemOf(folder: VaultDocumentFolderDto): 'arche-saves' | 'my-fias' | null {
  if (folder.path === ARCHE_SAVES_ROOT) return 'arche-saves';
  if (folder.path === MY_FIAS_ROOT) return 'my-fias';
  return null;
}

const RANK = { system: 0, folder: 1, file: 2 } as const;

/**
 * A container's items in order.
 *
 * On the desktop (no `view`) that is auto-arrange order: the system icons
 * (Arche Saves, My-Fias, Trash) first, then folders, then files — each by
 * name, then id. Total and stable, so the same files lay out the same way on
 * every machine.
 *
 * In a folder window (`view` given) it is the view's sort: system folders,
 * then folders by the view, then files in the order they came — the server
 * sorts them, since they arrive a page at a time.
 */
export function containerItems(input: {
  container: string;
  folders: readonly VaultDocumentFolderDto[];
  documents: readonly VaultDocumentDto[];
  trashFull?: boolean;
  view?: FolderView;
}): DesktopItem[] {
  const parentId = folderIdOfContainer(input.container);
  const entries: Array<{ item: DesktopItem; rank: number }> = [];
  for (const folder of input.folders) {
    if ((folder.parentFolderId ?? null) !== parentId) continue;
    const system = systemOf(folder);
    entries.push({
      item: { kind: 'folder', key: folder.folderId, name: folder.name, folder, system },
      rank: system ? RANK.system : RANK.folder,
    });
  }
  for (const doc of input.documents) {
    entries.push({
      item: { kind: 'file', key: doc.documentId, name: doc.name, doc },
      rank: RANK.file,
    });
  }
  if (input.container === ROOT_CONTAINER && input.trashFull !== undefined) {
    entries.push({
      item: { kind: 'trash', key: TRASH_KEY, name: 'Trash', full: !!input.trashFull },
      rank: RANK.system,
    });
  }
  const { view } = input;
  if (view) {
    const folderEntries = entries.filter((e) => e.item.kind !== 'file');
    const files = entries.filter((e) => e.item.kind === 'file').map((e) => e.item);
    folderEntries.sort((a, b) => {
      if (a.rank !== b.rank) return a.rank - b.rank;
      const fa = a.item.kind === 'folder' ? a.item.folder : null;
      const fb = b.item.kind === 'folder' ? b.item.folder : null;
      return compareFoldersForView(
        { name: a.item.name, updatedAt: fa?.updatedAt, key: a.item.key },
        { name: b.item.name, updatedAt: fb?.updatedAt, key: b.item.key },
        view,
      );
    });
    return [...folderEntries.map((e) => e.item), ...files];
  }
  entries.sort((a, b) =>
    compareArrangeOrder(
      { rank: a.rank, name: a.item.name, key: a.item.key },
      { rank: b.rank, name: b.item.name, key: b.item.key },
    ),
  );
  return entries.map((e) => e.item);
}

/** Columns that fit a canvas `width` px wide. */
export function columnsFor(width: number, g: CellGeometry = MEDIUM_CELLS): number {
  return Math.max(1, Math.floor((width - g.pad * 2) / g.cellW));
}

/** Rows that fit a canvas `height` px tall. */
export function rowsFor(height: number, g: CellGeometry = MEDIUM_CELLS): number {
  return Math.max(1, Math.floor((height - g.pad * 2 - (g.top ?? 0)) / g.cellH));
}

/** Top-left of a cell, in canvas px. */
export function cellOrigin(cell: Cell, g: CellGeometry = MEDIUM_CELLS): { x: number; y: number } {
  return { x: g.pad + cell.c * g.cellW, y: g.pad + (g.top ?? 0) + cell.r * g.cellH };
}

/** The cell under a canvas-local point (clamped into the grid). */
export function cellAtPoint(
  x: number,
  y: number,
  columns: number,
  g: CellGeometry = MEDIUM_CELLS,
): Cell {
  return {
    c: Math.min(Math.max(0, Math.floor((x - g.pad) / g.cellW)), Math.max(0, columns - 1)),
    r: Math.max(0, Math.floor((y - g.pad - (g.top ?? 0)) / g.cellH)),
  };
}

/**
 * Where the n-th auto-placed icon goes on the desktop. Windows fills rows
 * from the top-left; a Mac desktop stacks icons down from the top-RIGHT,
 * column by column.
 */
export function desktopSlot(
  style: 'windows' | 'mac',
  columns: number,
  rows: number,
): (index: number) => Cell {
  if (style === 'mac') {
    return (index) => ({
      c: Math.max(0, columns - 1 - Math.floor(index / rows)),
      r: index % rows,
    });
  }
  return (index) => ({ c: index % columns, r: Math.floor(index / columns) });
}

/** The occupied cell indexes of an arrangement, minus `except`. */
export function takenCells(
  positions: ReadonlyMap<string, Cell>,
  columns: number,
  except: ReadonlySet<string> = new Set(),
): Set<number> {
  const taken = new Set<number>();
  for (const [key, cell] of positions) {
    if (!except.has(key)) taken.add(cellIndex(cell, columns));
  }
  return taken;
}

/** The item in `direction` of `from` — the nearest one along that axis,
 *  preferring the same row / column — for arrow-key focus. */
export function neighbourInDirection(
  positions: ReadonlyMap<string, Cell>,
  from: string,
  direction: 'left' | 'right' | 'up' | 'down',
): string | null {
  const origin = positions.get(from);
  if (!origin) return null;
  let best: { key: string; score: number } | null = null;
  for (const [key, cell] of positions) {
    if (key === from) continue;
    const dc = cell.c - origin.c;
    const dr = cell.r - origin.r;
    const along =
      direction === 'left' ? -dc : direction === 'right' ? dc : direction === 'up' ? -dr : dr;
    const across = direction === 'left' || direction === 'right' ? Math.abs(dr) : Math.abs(dc);
    if (along <= 0) continue;
    const score = across * 1000 + along;
    if (!best || score < best.score) best = { key, score };
  }
  return best?.key ?? null;
}

/** `CSS.escape`, where the environment has it. Keys are `[a-z0-9_]` ids, so
 *  the fallback only has to guard quotes and backslashes. */
export function cssEscape(value: string): string {
  return typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
    ? CSS.escape(value)
    : value.replace(/["\\]/g, '\\$&');
}
